import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";

import { Home } from "@/pages/public/Home";
import { About } from "@/pages/public/About";
import { Features } from "@/pages/public/Features";
import { Pricing } from "@/pages/public/Pricing";
import { Results } from "@/pages/public/Results";
import { Blog } from "@/pages/public/Blog";
import { BlogPost } from "@/pages/public/BlogPost";
import { Contact } from "@/pages/public/Contact";
import { Login } from "@/pages/auth/Login";

import { AdminLayout } from "@/pages/admin/AdminLayout";
import { AdminOverview } from "@/pages/admin/AdminOverview";
import { AdminRestaurants } from "@/pages/admin/AdminRestaurants";
import { AdminBlogs } from "@/pages/admin/AdminBlogs";
import { AdminSubscriptions } from "@/pages/admin/AdminSubscriptions";

import { RestaurantLayout } from "@/pages/restaurant/RestaurantLayout";
import { RestaurantOverview } from "@/pages/restaurant/RestaurantOverview";
import { RestaurantMenu } from "@/pages/restaurant/RestaurantMenu";
import { RestaurantSettings } from "@/pages/restaurant/RestaurantSettings";
import { RestaurantQR } from "@/pages/restaurant/RestaurantQR";
import { RestaurantAnalytics } from "@/pages/restaurant/RestaurantAnalytics";
import { RestaurantFeedback } from "@/pages/restaurant/RestaurantFeedback";
import { RestaurantLoyalty } from "@/pages/restaurant/RestaurantLoyalty";

import { CustomerMenu } from "@/pages/customer/CustomerMenu";

const Protected = ({ role, children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-zq-muted">Loading…</div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          {/* Public marketing */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/results" element={<Results />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/login" element={<Login />} />

          {/* Customer QR menu */}
          <Route path="/m/:slug" element={<CustomerMenu />} />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <Protected role="admin">
                <AdminLayout />
              </Protected>
            }
          >
            <Route index element={<AdminOverview />} />
            <Route path="restaurants" element={<AdminRestaurants />} />
            <Route path="blogs" element={<AdminBlogs />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
          </Route>

          {/* Restaurant */}
          <Route
            path="/dashboard"
            element={
              <Protected role="restaurant">
                <RestaurantLayout />
              </Protected>
            }
          >
            <Route index element={<RestaurantOverview />} />
            <Route path="menu" element={<RestaurantMenu />} />
            <Route path="qr" element={<RestaurantQR />} />
            <Route path="analytics" element={<RestaurantAnalytics />} />
            <Route path="feedback" element={<RestaurantFeedback />} />
            <Route path="loyalty" element={<RestaurantLoyalty />} />
            <Route path="settings" element={<RestaurantSettings />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
